import React, { useEffect, useRef, useState } from 'react';
import { Loader2, Send, Plus, History, Info, HardHat, FileText, Receipt, TrendingUp, BookOpen, CalendarDays, X } from 'lucide-react';
import { toast } from 'sonner';
import { aiApi } from '../api';
import { ActionCard, ResultChip, ConfirmCard } from '../components/ForemanCards';

const newSessionId = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : `s-${Date.now()}-${Math.random().toString(36).slice(2)}`);
const greeting = () => { const h = new Date().getHours(); return h < 12 ? 'Good morning' : h < 17 ? 'Good afternoon' : 'Good evening'; };

const QUICK = [
  { icon: FileText, label: 'Create Quote', msg: 'Create a new draft quote for ' },
  { icon: HardHat, label: 'New Project', msg: 'Create a project from an accepted quote' },
  { icon: Receipt, label: 'Add Expense', msg: 'Add an expense: ' },
  { icon: TrendingUp, label: 'Check Profit', msg: 'What is our current profit picture across projects?' },
  { icon: BookOpen, label: 'Price Book', msg: 'Audit my price book for duplicates, issues, and missing common items' },
  { icon: CalendarDays, label: 'Schedule', msg: "What's on the calendar this week?" },
];

const Avatar = () => (
  <span className="h-7 w-7 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/50 flex items-center justify-center shrink-0">
    <HardHat size={13} className="text-[#D4AF37]" />
  </span>
);

export default function OpsAssistant() {
  const [sessionId, setSessionId] = useState(newSessionId);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [confirmBusy, setConfirmBusy] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [drawer, setDrawer] = useState(null); // 'history' | 'info' | null
  const endRef = useRef(null);
  const inputRef = useRef(null);

  const loadSessions = () => aiApi.sessions().then((r) => setSessions(r.data)).catch(() => {});
  useEffect(() => { loadSessions(); }, []);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages, busy]);

  const openSession = (sid) => {
    setSessionId(sid); setDrawer(null);
    aiApi.messages(sid).then((r) => setMessages(r.data)).catch(() => setMessages([]));
  };

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || busy) return;
    setInput('');
    setMessages((m) => [...m, { role: 'user', content: msg, id: `u-${Date.now()}` }]);
    setBusy(true);
    try {
      const r = await aiApi.chat(sessionId, msg);
      setMessages((m) => [...m, {
        role: 'assistant', content: r.data.reply, id: `a-${Date.now()}`,
        actions: r.data.action_results || [], pending: r.data.pending_actions || [],
      }]);
      loadSessions();
    } catch (e) {
      const d = e.response?.data?.detail;
      setMessages((m) => [...m, { role: 'assistant', content: typeof d === 'string' ? d : 'Something went wrong — please try again.', id: `e-${Date.now()}`, error: true }]);
    } finally { setBusy(false); }
  };

  const resolvePending = async (msgId, action, approved) => {
    setConfirmBusy(true);
    try {
      const r = await aiApi.confirm(sessionId, action, approved);
      setMessages((m) => m.map((x) => x.id === msgId
        ? { ...x, pending: (x.pending || []).filter((p) => p !== action), actions: [...(x.actions || []), r.data.cancelled ? { ok: true, detail: 'Cancelled — nothing was created' } : r.data] }
        : x));
      if (!r.data.cancelled && r.data.ok) toast.success(r.data.detail);
      if (!r.data.cancelled && !r.data.ok) toast.error(r.data.detail);
    } catch { toast.error('Could not process the confirmation'); } finally { setConfirmBusy(false); }
  };

  const cardAction = async (action) => {
    setConfirmBusy(true);
    try {
      const r = await aiApi.confirm(sessionId, action, true);
      if (r.data.ok) { toast.success(r.data.detail); return r.data.detail; }
      toast.error(r.data.detail); return null;
    } catch { toast.error('Action failed'); return null; } finally { setConfirmBusy(false); }
  };

  const fresh = messages.length === 0;

  return (
    <div className="max-w-3xl mx-auto flex flex-col h-[calc(100vh-120px)]" data-testid="ops-assistant-page">
      {/* Compact header */}
      <div className="flex items-center justify-between gap-3 pb-3 border-b border-gray-800/70">
        <div className="flex items-center gap-2.5">
          <Avatar />
          <div>
            <div className="flex items-center gap-2">
              <span className="font-brand font-black text-white text-sm tracking-wide">OPS FOREMAN</span>
              <span className="flex items-center gap-1 text-[10px] text-green-400" data-testid="foreman-connected"><span className="h-1.5 w-1.5 rounded-full bg-green-500 animate-pulse" /> Connected</span>
            </div>
            <span className="text-[10px] text-gray-500">BuildBroz Operations AI</span>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={() => { setSessionId(newSessionId()); setMessages([]); setDrawer(null); }} className="p-2 rounded-lg text-gray-400 hover:text-[#D4AF37] hover:bg-white/5 transition-colors" title="New Chat" data-testid="ai-new-chat-btn"><Plus size={17} /></button>
          <button onClick={() => setDrawer(drawer === 'history' ? null : 'history')} className={`p-2 rounded-lg transition-colors ${drawer === 'history' ? 'text-[#D4AF37] bg-white/5' : 'text-gray-400 hover:text-[#D4AF37] hover:bg-white/5'}`} title="Chat History" data-testid="ai-sessions-btn"><History size={16} /></button>
          <button onClick={() => setDrawer(drawer === 'info' ? null : 'info')} className={`p-2 rounded-lg transition-colors ${drawer === 'info' ? 'text-[#D4AF37] bg-white/5' : 'text-gray-400 hover:text-[#D4AF37] hover:bg-white/5'}`} title="Assistant Info" data-testid="ai-info-btn"><Info size={16} /></button>
        </div>
      </div>

      {drawer === 'history' && (
        <div className="border-b border-gray-800/70 py-2 max-h-44 overflow-y-auto" data-testid="ai-sessions-list">
          {sessions.length === 0 ? <p className="text-gray-600 text-xs px-2 py-1.5">No previous chats.</p> : sessions.map((s) => (
            <button key={s.session_id} onClick={() => openSession(s.session_id)} className="w-full text-left px-2.5 py-1.5 rounded hover:bg-white/5 text-xs text-gray-400 truncate" data-testid={`ai-session-${s.session_id}`}>
              <span className="text-gray-600">{new Date(s.last_at).toLocaleDateString()} · </span>{s.preview}
            </button>
          ))}
        </div>
      )}
      {drawer === 'info' && (
        <div className="border-b border-gray-800/70 py-3 px-2 text-xs text-gray-400 leading-relaxed" data-testid="ai-info-panel">
          <div className="flex justify-between items-start"><span className="text-[#D4AF37] font-brand font-bold uppercase tracking-wider text-[10px]">What Ops Foreman can do</span><button onClick={() => setDrawer(null)}><X size={13} className="text-gray-600" /></button></div>
          <p className="mt-1.5">Sees all of Ops: projects, quotes (full line detail), price book, employees, subs, expenses, calendar, pay stubs, leads.</p>
          <p className="mt-1"><span className="text-gray-300">Can do instantly:</span> create draft quotes, add/edit/remove quote lines (price book or custom), edit drafts, projects, price book, employee pay.</p>
          <p className="mt-1"><span className="text-gray-300">Asks first:</span> creating projects from accepted quotes, expenses, calendar events.</p>
          <p className="mt-1"><span className="text-red-400/90">Never:</span> contacts customers, sends/publishes quotes or links, accepts quotes, moves money, deletes financial history.</p>
        </div>
      )}

      {/* Conversation */}
      <div className="flex-1 overflow-y-auto py-4 space-y-5" data-testid="ai-messages">
        {fresh && !busy ? (
          <div className="pt-10 px-1" data-testid="foreman-home">
            <h2 className="text-2xl sm:text-3xl font-brand font-black text-white">{greeting()}, Roman.</h2>
            <p className="text-gray-500 mt-1 text-base md:text-lg">What are we working on?</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-6 max-w-md">
              {QUICK.map((q) => (
                <button key={q.label} onClick={() => { if (q.msg.endsWith(' ')) { setInput(q.msg); inputRef.current?.focus(); } else send(q.msg); }}
                  className="flex items-center gap-2 bg-[#161616] hover:bg-[#1D1D1D] border border-gray-800/70 rounded-xl px-3 py-2.5 text-xs text-gray-300 font-medium transition-colors" data-testid={`quick-${q.label.toLowerCase().replace(/\s/g, '-')}`}>
                  <q.icon size={14} className="text-[#D4AF37] shrink-0" /> {q.label}
                </button>
              ))}
            </div>
          </div>
        ) : messages.map((m) => (
          m.role === 'user' ? (
            <div key={m.id || m.created_at} className="flex justify-end">
              <div className="max-w-[80%] bg-[#1E1E1E] text-gray-100 text-sm rounded-2xl rounded-br-md px-4 py-2.5 whitespace-pre-wrap leading-relaxed">{m.content}</div>
            </div>
          ) : (
            <div key={m.id || m.created_at} className="flex gap-2.5">
              <Avatar />
              <div className="min-w-0 flex-1">
                <span className="font-brand text-[9px] font-bold uppercase tracking-[0.18em] text-gray-500">Ops Foreman</span>
                <div className={`text-sm whitespace-pre-wrap leading-relaxed mt-0.5 ${m.error ? 'text-red-400' : 'text-gray-200'}`}>{m.content}</div>
                {(m.actions || []).length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5">{m.actions.map((a, i) => <ResultChip key={i} r={a} />)}</div>
                )}
                {(m.actions || []).filter((a) => a.card).map((a, i) => <div key={`c${i}`} className="mt-2.5"><ActionCard card={a.card} onAction={cardAction} busy={confirmBusy} /></div>)}
                {(m.pending || []).map((p, i) => (
                  <div key={`p${i}`} className="mt-2.5">
                    <ConfirmCard action={p} busy={confirmBusy} onConfirm={() => resolvePending(m.id, p, true)} onCancel={() => resolvePending(m.id, p, false)} />
                  </div>
                ))}
              </div>
            </div>
          )
        ))}
        {busy && <div className="flex items-center gap-2.5"><Avatar /><span className="text-gray-500 text-sm flex items-center gap-2"><Loader2 size={14} className="animate-spin text-[#D4AF37]" /> Working on it…</span></div>}
        <div ref={endRef} />
      </div>

      {/* Sticky composer */}
      <div className="sticky bottom-0 pb-2 pt-2 bg-gradient-to-t from-black via-black/95 to-transparent">
        <div className="flex items-center gap-2">
          <button onClick={() => { setSessionId(newSessionId()); setMessages([]); }} className="h-9 w-9 rounded-full bg-[#161616] border border-gray-800 flex items-center justify-center text-gray-400 hover:text-[#D4AF37] transition-colors shrink-0" title="New chat" data-testid="composer-plus-btn"><Plus size={16} /></button>
          <input ref={inputRef} value={input} onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') send(); }}
            placeholder="Ask Ops Foreman anything..."
            className="flex-1 bg-[#161616] border border-gray-800 rounded-full px-4 py-2.5 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-[#D4AF37]/50 transition-colors"
            data-testid="ai-chat-input" />
          <button onClick={() => send()} disabled={busy || !input.trim()}
            className="h-10 w-10 rounded-full bg-gradient-to-b from-[#F0CB5C] to-[#C99E2E] hover:from-[#D4AF37] hover:to-[#B8941F] flex items-center justify-center text-black disabled:opacity-40 transition-colors shrink-0" data-testid="ai-send-btn">
            {busy ? <Loader2 size={16} className="animate-spin" /> : <Send size={15} />}
          </button>
        </div>
      </div>
    </div>
  );
}
